import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

const Settings = () => {
  const [eprType, setEprType] = useState<"EPIC" | "Cerner" | "CareFlow">("EPIC");
  const [isLoading, setIsLoading] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from("epr_settings")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setEprType(data.epr_type as "EPIC" | "Cerner" | "CareFlow");
          setCurrentRecordId(data.id);
        } else {
          // If no settings exist, create default settings with EPIC
          const { data: newData, error: insertError } = await supabase
            .from("epr_settings")
            .insert({ epr_type: "EPIC" })
            .select()
            .single();

          if (insertError) throw insertError;
          setEprType("EPIC");
          setCurrentRecordId(newData.id);
        }
      } catch (error) {
        console.error("Error fetching EPR settings:", error);
        toast.error("Failed to load EPR settings");
      }
    };

    fetchSettings();
  }, [toast]);

  const handleEPRChange = (value: string) => {
    setEprType(value as "EPIC" | "Cerner" | "CareFlow");
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const upsertData = currentRecordId ? { id: currentRecordId, epr_type: eprType } : { epr_type: eprType };

      const { data, error } = await supabase.from("epr_settings").upsert(upsertData).select().single();

      if (error) throw error;

      // Update the current record ID if we didn't have one before
      if (!currentRecordId && data) {
        setCurrentRecordId(data.id);
      }

      // Invalidate the EPR settings query to force refetch
      await queryClient.invalidateQueries({
        queryKey: ["epr-settings"],
      });

      toast.success(`EPR type saved as ${eprType}`);
    } catch (error) {
      console.error("Error saving EPR settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>EPR Configuration</CardTitle>
          <CardDescription>Select your Electronic Patient Record system type or visual theme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup value={eprType} onValueChange={handleEPRChange} className="space-y-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="EPIC" id="epic" />
              <Label htmlFor="epic">EPIC</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Cerner" id="cerner" />
              <Label htmlFor="cerner">Cerner</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="CareFlow" id="careflow" />
              <Label htmlFor="careflow">CareFlow</Label>
            </div>
          </RadioGroup>

          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
